/* eslint-disable */

import { get, isEqual, uniqBy, cloneDeep } from 'lodash';
import { mapActions, mapGetters, mapState } from 'vuex';
import marketContractsStats from '@/components/market/market-contracts-stats/market-contracts-stats.vue';
import chartsContainer from '@/components/market/market-detail-charts/container/container.vue';
import chartsHeader from '@/components/market/market-detail-charts/header/header.vue';
import chartsTable from '@/components/market/market-detail-charts/table/table.vue';
import chartsTimeline from '@/components/market/market-detail-charts/timeline/timeline.vue';
import env from '@/env';
import { pushStoryInteraction, INTERACTION_TYPE } from '@/helpers/dataLayer';
import log from '@/logger';
import contentLoaderMixin from '@/mixins/contentLoader';
import loaderMixin from '@/mixins/loader';
import { groupByKeepOrder, hasMarketHiddenContracts, isHighChartsReady } from '@/utils/helpers';
import { isMarketSingle } from '@/utils/helpers';
import { isLiveChartEligible } from './live/live-chart-policy';
import { regularContractPrice } from './utils';

// const YES_CONTRACT_TYPE = 'Yes';
// const NO_CONTRACT_TYPE = 'No';
// const NO_CONTRACT_LINE_COLOR = '#F27130';
const MARKET_SINGLE_CONTRACT_NAME = 'Closing Price';
const INITIAL_SHOW_CONTRACTS = 3;
export const INITIAL_MAX_CONTRACTS = 6;

const CHART_TYPES = {
  LINE: 'line',
  CANDLE: 'candle',
};

export default {
  mixins: [loaderMixin, contentLoaderMixin],
  props: {
    liveUpdatesEnabled: { type: Boolean, default: () => env.CHARTS_LIVE_UPDATES_ENABLED },
    liveUpdatesPlacement: { type: String, default: 'market' },
    market: {
      type: Object,
      required: false,
    },
    chartOptions: {
      type: Object,
      default: null,
    },
    theme: {
      type: String,
      default: '',
    },
    cache: {
      type: Boolean,
      default: false,
    },
    chartSystemOptions: {
      type: Object,
      default: () => {},
    },
    suppressGlobalLoader: {
      type: Boolean,
      default: false,
    },
    trackingContext: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    const contractIds = get(this.chartOptions, 'contractIds', []) || [];

    return {
      hasMarketHiddenContracts,
      initialised: false,
      chartRequestGeneration: 0,
      chartDisposed: false,
      pendingChartRequests: 0,
      liveHistoryReady: false,
      chartHistoryLoading: false,
      chartDataTimeline: null,
      chartType: CHART_TYPES.LINE,
      preparedContractIds: null,
      toggled: false,
      disabledContracts: new Set(),
      availableContractIds: contractIds,
      chartShowContractsInitial:
        contractIds.length > 0 ? contractIds.length : INITIAL_SHOW_CONTRACTS,
      chartMaxContracts: contractIds.length > 0 ? null : INITIAL_MAX_CONTRACTS,
      chartTimeline: null,
      chartDataLoaded: false,
      chartData: [],
      originalChartData: [],
      showStats: false,
      showVolume: false,
      showPriceAxisZoom: true,
      volumeToggleInitialized: false,
      priceAxisZoomToggleInitialized: false,
      timelineToggleInitialized: false,
    };
  },
  components: {
    chartsContainer,
    chartsHeader,
    chartsTable,
    chartsTimeline,
    marketContractsStats,
  },
  computed: {
    liveUpdatesEligible() {
      return isLiveChartEligible({
        enabled: this.liveUpdatesEnabled,
        placement: this.liveUpdatesPlacement,
        chartType: this.chartType,
        timeline: this.chartTimeline,
        market: this.market,
      });
    },
    ...mapGetters('app', ['isLayoutDesktop', 'isLayoutMobile']),
    ...mapState('market', ['marketContractsStats']),
    classModifiers() {
      return {
        'market-detail-charts': true,
        [`market-detail-charts--${this.theme}`]: this.theme,
      };
    },
    title() {
      return get(this.chartOptions, 'chartTitle', null);
    },
    caption() {
      return get(this.chartOptions, 'chartCaption', null);
    },
    chartContracts() {
      const results = [];
      const contractIdDataGroup = groupByKeepOrder(this.chartData, 'contractId');

      // show contracts that represent selected point on time on the chart
      contractIdDataGroup.forEach((data) => {
        results.push(data[data.length - 1]);
      });

      const marketContracts = cloneDeep(this.market.contracts || this.market.marketContracts);
      return results.map((item) => {
        const contract = marketContracts.find((i) => i.contractId === item.contractId) || {};
        return {
          ...item,
          contractImageUrl: contract.contractImageUrl,
          contractImageSmallUrl: contract.contractImageSmallUrl,
        };
      });
    },
    showChartsTable() {
      const noChartOptionOrLegendOn =
        this.chartOptions === null || get(this.chartOptions, 'includeLegend', false);
      return this.chartContracts.length > 1 && noChartOptionOrLegendOn;
    },
    filteredChartData() {
      return this.chartData.filter((el) => el.checked);
    },
  },
  methods: {
    ...mapActions('chart', ['getMarketChartData']),
    ...mapActions('market', ['getMarketContractsStats', 'clearMarketContractsStats']),
    prepareDisabledContracts(data) {
      if (this.availableContractIds.length > 0) {
        this.disabledContracts = new Set(
          uniqBy(data, (el) => el.contractId)
            .map((el) => el.contractId)
            .filter((i) => !this.availableContractIds.includes(i)),
        );
      } else {
        const results = [];
        const contractIdDataGroup = groupByKeepOrder(data, 'contractId');
        // show contracts that represent selected point on time on the art
        contractIdDataGroup.forEach((groupData) => {
          results.push(groupData[groupData.length - 1]);
        });

        results.forEach((el, index) => {
          if (index >= this.chartShowContractsInitial) {
            this.disabledContracts.add(el.contractId);
          }
        });
      }
    },
    prepareChartData(source) {
      const data = cloneDeep(source);
      const contractIds =
        this.availableContractIds.length > 0
          ? this.availableContractIds
          : uniqBy(data, (el) => el.contractId).map((el) => el.contractId);

      const isYesNoMarket = isMarketSingle(this.market);
      if (!isEqual(contractIds, this.preparedContractIds)) {
        this.prepareDisabledContracts(data);
        this.preparedContractIds = contractIds;
      }

      data.forEach((el) => {
        el.additional = false;
        el.checked = this.checkIsContractEnabled(el);
        el.chartPrice = regularContractPrice(el);

        if (isYesNoMarket) {
          el.contractName = MARKET_SINGLE_CONTRACT_NAME;
        }
      });

      return data;
    },
    checkIsContractEnabled(el) {
      return !this.disabledContracts.has(el.contractId);
    },
    getChartLoaderTarget() {
      return this.$refs.chartLoadingScope || this.$el;
    },
    beginChartRequestLoad() {
      const globalMobile = !this.suppressGlobalLoader && this.isLayoutMobile;
      const target = this.suppressGlobalLoader ? this.getChartLoaderTarget() : this.$el;
      this.pendingChartRequests += 1;
      this.isLoading = true;
      if (globalMobile) this.incrNewLoaderCounter();
      else target?.classList?.add('is-loading');
      let ended = false;
      return () => {
        if (ended) return;
        ended = true;
        if (globalMobile) this.decrNewLoaderCounter();
        this.pendingChartRequests -= 1;
        this.isLoading = this.pendingChartRequests > 0;
        if (!this.isLoading) target?.classList?.remove('is-loading');
      };
    },
    loadStats() {
      const endLoad = this.beginChartRequestLoad();
      this.getMarketContractsStats(this.market.marketId)
        .catch(() => {})
        .finally(endLoad);
    },
    loadChartData({ useOriginalData = false }) {
      if (useOriginalData) {
        this.chartData = this.prepareChartData(this.originalChartData);
      } else {
        const generation = ++this.chartRequestGeneration;
        this.liveHistoryReady = false;
        this.chartHistoryLoading = true;
        const params = {
          marketId: this.market.marketId,
          maxContracts: this.chartMaxContracts,
          timespan: this.chartTimeline,
        };
        const storyId = get(this.chartOptions, 'cmsStoryId', null);
        if (storyId) {
          params.storyId = storyId;
          params.chartStoryVersionId = get(this.chartOptions, 'cmsVersionId', null);
          params.regionUUID = get(this.chartOptions, 'regionUUID', null);
          params.sectionType = get(this.chartOptions, 'sectionType', null);
        }
        const endLoad = this.beginChartRequestLoad();
        this.getMarketChartData({ params, cache: this.cache })
          .then((data) => {
            if (this.chartDisposed || generation !== this.chartRequestGeneration) return;
            this.originalChartData = [...data];
            this.chartData = this.prepareChartData(this.originalChartData);
            this.chartDataTimeline = params.timespan;
            this.liveHistoryReady = true;
            if (this.chartData.length === 0 && !this.initialised) {
              this.$emit('no-data');
            }
            this.chartDataLoaded = true;
            this.isComponentReady = isHighChartsReady();
            this.initialised = true;
          })
          .catch((e) => {
            if (this.chartDisposed || generation !== this.chartRequestGeneration) return;
            console.error('Chart error', e);
            this.initialised = true;
          })
          .finally(() => {
            if (!this.chartDisposed && generation === this.chartRequestGeneration)
              this.chartHistoryLoading = false;
            endLoad();
          });
      }
    },
    onShowKey() {
      this.toggled = true;
    },
    fireChartInteraction() {
      if (!this.trackingContext.storyId) return;
      pushStoryInteraction({
        interactionType: INTERACTION_TYPE.CHART_INTERACTION,
        ...this.trackingContext,
      });
    },
    volumeChartToggle(value) {
      this.showVolume = value;
      if (!this.volumeToggleInitialized) {
        this.volumeToggleInitialized = true;
        return;
      }
      this.fireChartInteraction();
    },
    priceAxisZoomToggle(value) {
      this.showPriceAxisZoom = value;
      if (!this.priceAxisZoomToggleInitialized) {
        this.priceAxisZoomToggleInitialized = true;
        return;
      }
      this.fireChartInteraction();
    },
    onTimelineChange(timeline) {
      if (!this.timelineToggleInitialized) {
        this.timelineToggleInitialized = true;
      } else {
        this.fireChartInteraction();
      }
      if (timeline === 'stats') {
        this.showStats = true;
        this.loadStats();
      } else {
        this.showStats = false;
        this.chartTimeline = timeline;
        this.loadChartData({});
      }
    },
    onTypeChange(type) {
      this.fireChartInteraction();
      this.chartType = type;

      if (type === CHART_TYPES.CANDLE && this.chartData) {
        const contracts = uniqBy(this.chartData, (el) => el.contractId);
        const enabled = contracts.find((i) => this.checkIsContractEnabled(i)) || contracts[0];
        const contractIds = contracts
          .map((i) => i.contractId)
          .filter((i) => i !== enabled.contractId);
        this.disabledContracts = new Set(contractIds);
        this.loadChartData({ useOriginalData: true });
      }
    },
    onContractToggle(item, checked) {
      this.fireChartInteraction();
      const contractId = item.contractId;
      // const contractsNum = Math.min(this.market.contracts.length, INITIAL_MAX_CONTRACTS);
      // const disabledContractsMaxSize = contractsNum;

      if (this.chartType === CHART_TYPES.CANDLE) {
        if (!checked) {
          this.disabledContracts.add(contractId);
        } else {
          const contractIds = uniqBy(this.chartData, (el) => el.contractId)
            .map((i) => i.contractId)
            .filter((i) => i !== contractId);
          this.disabledContracts = new Set(contractIds);
        }
      } else {
        if (!checked) {
          this.disabledContracts.add(contractId);
        } else {
          this.disabledContracts.delete(contractId);
        }
      }

      this.loadChartData({ useOriginalData: true });
    },
  },
  watch: {
    'market.marketId': {
      flush: 'sync',
      handler() {
        this.liveHistoryReady = false;
        this.chartRequestGeneration += 1;
        this.chartData = [];
        this.originalChartData = [];
        this.chartDataTimeline = null;
        this.preparedContractIds = null;
        this.disabledContracts = new Set();
        if (this.chartTimeline) this.loadChartData({});
      },
    },
  },
  created() {
    log.info(
      `[CHART] Rendering chart: ${JSON.stringify(this.chartOptions || {})} for ${this.market.marketId}`,
    );
  },
  beforeUnmount() {
    this.chartDisposed = true;
    this.chartRequestGeneration += 1;
    this.liveHistoryReady = false;
    this.clearMarketContractsStats();
  },
};
